import type { KnowledgeItemData } from "./api";
import {
  getKnowledgeAiPreparationFailureMessage,
  getKnowledgeFailureDisplayMessage,
  getKnowledgeItemDisplayName,
  getKnowledgeTypeLabel,
  getKnowledgeUnavailableReason,
  isKnowledgeItemAiPreparationFailed,
  isKnowledgeItemGroupTalkUsable,
  isKnowledgeItemPending,
} from "./knowledge";
import {
  removePendingJobNotification,
  writePendingJobNotification,
} from "./jobNotifications";

export const KNOWLEDGE_BACKGROUND_STORAGE_KEY = "wizmate.knowledge-background.v1";
export const KNOWLEDGE_BACKGROUND_EVENT = "wizmate:knowledge-background";
export const MAX_PENDING_KNOWLEDGE_ITEMS_PER_USER = 20;
export const MAX_KNOWLEDGE_REPORTS_PER_USER = 40;

export type KnowledgeBackgroundType = "pdf" | "url" | "executive_memo" | "company_research";

export type PendingKnowledgeBackgroundItem = {
  knowledgeItemId: string;
  type: KnowledgeBackgroundType;
  title: string;
  startedAt: string;
};

export type KnowledgeBackgroundReport = {
  knowledgeItemId: string;
  type: KnowledgeBackgroundType;
  title: string;
  status: "completed" | "failed";
  occurredAt: string;
  failureMessage?: string;
};

export type KnowledgeHomeActivityLog = {
  id: string;
  text: string;
  time: string;
  activityAt: string;
  to: string;
  isActive: boolean;
};

type KnowledgeBackgroundUserState = {
  pending: PendingKnowledgeBackgroundItem[];
  reports: KnowledgeBackgroundReport[];
};

type KnowledgeBackgroundState = {
  version: 1;
  users: Record<string, KnowledgeBackgroundUserState>;
};

type KnowledgeBackgroundResolution =
  | { status: "processing" }
  | { status: "completed" }
  | { status: "failed"; failureMessage: string }
  | { status: "deleted" };

function emptyState(): KnowledgeBackgroundState {
  return { version: 1, users: {} };
}

function hasStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function normalizeText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return normalized || null;
}

function normalizeUserId(value: unknown): string | null {
  return normalizeText(value);
}

function normalizeType(value: unknown): KnowledgeBackgroundType | null {
  return value === "pdf" || value === "url" || value === "executive_memo" || value === "company_research"
    ? value
    : null;
}

function normalizeIso(value: unknown): string | null {
  const normalized = normalizeText(value);
  if (!normalized) return null;
  const timestamp = Date.parse(normalized);
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : null;
}

function sanitizeTitle(type: KnowledgeBackgroundType, value: unknown): string {
  const title = normalizeText(value);
  const safeTitle = getKnowledgeItemDisplayName({
    type,
    title: title ?? "",
    original_filename: null,
  });
  return Array.from(safeTitle || getKnowledgeTypeLabel(type)).slice(0, 255).join("");
}

function sanitizeFailureMessage(value: unknown): string | undefined {
  const message = normalizeText(value);
  if (!message) return undefined;
  if (/(?:https?:|www\.|\/\/|%2f|\\|[?#][^\s=]*=|(?:token|secret|key)=)/iu.test(message)) {
    return "資料の状態を確認してください。";
  }
  return Array.from(message).slice(0, 500).join("");
}

function parsePending(value: unknown): PendingKnowledgeBackgroundItem | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Partial<PendingKnowledgeBackgroundItem>;
  const knowledgeItemId = normalizeText(candidate.knowledgeItemId);
  const type = normalizeType(candidate.type);
  const startedAt = normalizeIso(candidate.startedAt);
  if (!knowledgeItemId || !type || !startedAt) return null;
  return {
    knowledgeItemId,
    type,
    title: sanitizeTitle(type, candidate.title),
    startedAt,
  };
}

function parseReport(value: unknown): KnowledgeBackgroundReport | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Partial<KnowledgeBackgroundReport>;
  const knowledgeItemId = normalizeText(candidate.knowledgeItemId);
  const type = normalizeType(candidate.type);
  const occurredAt = normalizeIso(candidate.occurredAt);
  const status = candidate.status === "completed" || candidate.status === "failed" ? candidate.status : null;
  if (!knowledgeItemId || !type || !occurredAt || !status) return null;
  return {
    knowledgeItemId,
    type,
    title: sanitizeTitle(type, candidate.title),
    status,
    occurredAt,
    failureMessage: status === "failed" ? sanitizeFailureMessage(candidate.failureMessage) : undefined,
  };
}

function readState(): KnowledgeBackgroundState {
  if (!hasStorage()) return emptyState();
  try {
    const parsed = JSON.parse(window.localStorage.getItem(KNOWLEDGE_BACKGROUND_STORAGE_KEY) ?? "null") as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return emptyState();
    const candidate = parsed as { version?: unknown; users?: unknown };
    if (candidate.version !== 1 || !candidate.users || typeof candidate.users !== "object" || Array.isArray(candidate.users)) {
      return emptyState();
    }

    const users: Record<string, KnowledgeBackgroundUserState> = {};
    Object.entries(candidate.users as Record<string, unknown>).forEach(([rawUserId, rawState]) => {
      const userId = normalizeUserId(rawUserId);
      if (!userId || !rawState || typeof rawState !== "object" || Array.isArray(rawState)) return;
      const state = rawState as { pending?: unknown; reports?: unknown };
      const pending = Array.isArray(state.pending)
        ? state.pending.map(parsePending).filter((item): item is PendingKnowledgeBackgroundItem => item !== null)
        : [];
      const reports = Array.isArray(state.reports)
        ? state.reports.map(parseReport).filter((item): item is KnowledgeBackgroundReport => item !== null)
        : [];
      users[userId] = {
        pending: pending.slice(0, MAX_PENDING_KNOWLEDGE_ITEMS_PER_USER),
        reports: reports.slice(0, MAX_KNOWLEDGE_REPORTS_PER_USER),
      };
    });
    return { version: 1, users };
  } catch {
    return emptyState();
  }
}

function writeState(state: KnowledgeBackgroundState): void {
  if (!hasStorage()) return;
  try {
    window.localStorage.setItem(KNOWLEDGE_BACKGROUND_STORAGE_KEY, JSON.stringify(state));
    window.dispatchEvent(new CustomEvent(KNOWLEDGE_BACKGROUND_EVENT));
  } catch {
    // Storageが利用できない場合もKnowledge本体の操作は継続する。
  }
}

function currentUserState(state: KnowledgeBackgroundState, userId: string): KnowledgeBackgroundUserState {
  return state.users[userId] ?? { pending: [], reports: [] };
}

function safeItemTitle(item: KnowledgeItemData): string {
  const type = normalizeType(item.type) ?? "pdf";
  return sanitizeTitle(type, getKnowledgeItemDisplayName(item));
}

export function getKnowledgeBackgroundResolution(item: KnowledgeItemData): KnowledgeBackgroundResolution {
  if (item.status === "deleted") return { status: "deleted" };
  if (isKnowledgeItemGroupTalkUsable(item)) return { status: "completed" };
  if (isKnowledgeItemAiPreparationFailed(item)) {
    return {
      status: "failed",
      failureMessage: getKnowledgeAiPreparationFailureMessage(item.ai_preparation_failure_code),
    };
  }
  if (item.status === "failed") {
    return { status: "failed", failureMessage: getKnowledgeFailureDisplayMessage(item) };
  }
  if (isKnowledgeItemPending(item)) return { status: "processing" };
  return {
    status: "failed",
    failureMessage: getKnowledgeUnavailableReason(item) || "この資料はAIでは利用できません。",
  };
}

export function readPendingKnowledgeBackgroundItems(
  rawUserId: string | null | undefined
): PendingKnowledgeBackgroundItem[] {
  const userId = normalizeUserId(rawUserId);
  if (!userId) return [];
  return currentUserState(readState(), userId).pending;
}

export function readKnowledgeBackgroundReports(
  rawUserId: string | null | undefined
): KnowledgeBackgroundReport[] {
  const userId = normalizeUserId(rawUserId);
  if (!userId) return [];
  return currentUserState(readState(), userId).reports;
}

export function startKnowledgeBackgroundTracking(
  rawUserId: string | null | undefined,
  item: KnowledgeItemData,
  startedAt = new Date().toISOString()
): void {
  const userId = normalizeUserId(rawUserId);
  const type = normalizeType(item.type);
  const normalizedStartedAt = normalizeIso(startedAt);
  if (!userId || !type || !normalizeText(item.id) || !normalizedStartedAt) return;

  const tracked: PendingKnowledgeBackgroundItem = {
    knowledgeItemId: item.id,
    type,
    title: safeItemTitle(item),
    startedAt: normalizedStartedAt,
  };
  const state = readState();
  const userState = currentUserState(state, userId);
  const nextPending = [tracked, ...userState.pending.filter((entry) => entry.knowledgeItemId !== item.id)]
    .slice(0, MAX_PENDING_KNOWLEDGE_ITEMS_PER_USER);
  const retainedIds = new Set(nextPending.map((entry) => entry.knowledgeItemId));
  userState.pending
    .filter((entry) => !retainedIds.has(entry.knowledgeItemId))
    .forEach((entry) => {
      removePendingJobNotification({
        kind: "knowledge",
        userId,
        knowledgeItemId: entry.knowledgeItemId,
        status: "processing",
        title: entry.title,
        type: entry.type,
      });
    });
  state.users[userId] = {
    ...userState,
    pending: nextPending,
  };
  writeState(state);
  writePendingJobNotification({
    kind: "knowledge",
    userId,
    knowledgeItemId: item.id,
    status: "processing",
    title: tracked.title,
    type,
  });

  updateTrackedKnowledgeBackgroundItem(userId, item);
}

export function updateTrackedKnowledgeBackgroundItem(
  rawUserId: string | null | undefined,
  item: KnowledgeItemData
): KnowledgeBackgroundResolution | null {
  const userId = normalizeUserId(rawUserId);
  if (!userId) return null;
  const state = readState();
  const userState = currentUserState(state, userId);
  const tracked = userState.pending.find((entry) => entry.knowledgeItemId === item.id);
  if (!tracked) return null;

  const resolution = getKnowledgeBackgroundResolution(item);
  if (resolution.status === "processing") return resolution;
  if (resolution.status === "deleted") {
    state.users[userId] = {
      ...userState,
      pending: userState.pending.filter((entry) => entry.knowledgeItemId !== item.id),
    };
    writeState(state);
    removePendingJobNotification({
      kind: "knowledge",
      userId,
      knowledgeItemId: item.id,
      status: "processing",
      title: tracked.title,
      type: tracked.type,
    });
    return resolution;
  }

  const occurredAt = new Date().toISOString();
  const title = safeItemTitle(item) || tracked.title;
  const report: KnowledgeBackgroundReport = {
    knowledgeItemId: item.id,
    type: tracked.type,
    title,
    status: resolution.status,
    occurredAt,
    failureMessage: resolution.status === "failed" ? sanitizeFailureMessage(resolution.failureMessage) : undefined,
  };
  state.users[userId] = {
    pending: userState.pending.filter((entry) => entry.knowledgeItemId !== item.id),
    reports: [report, ...userState.reports].slice(0, MAX_KNOWLEDGE_REPORTS_PER_USER),
  };
  writeState(state);
  writePendingJobNotification({
    kind: "knowledge",
    userId,
    knowledgeItemId: item.id,
    status: resolution.status,
    processedAt: occurredAt,
    title,
    type: tracked.type,
    failedError: resolution.status === "failed" ? sanitizeFailureMessage(resolution.failureMessage) : undefined,
  });
  return resolution;
}

export function stopKnowledgeBackgroundTracking(
  rawUserId: string | null | undefined,
  knowledgeItemId: string
): void {
  const userId = normalizeUserId(rawUserId);
  const normalizedId = normalizeText(knowledgeItemId);
  if (!userId || !normalizedId) return;
  const state = readState();
  const userState = currentUserState(state, userId);
  const tracked = userState.pending.find((entry) => entry.knowledgeItemId === normalizedId);
  if (!tracked) return;
  state.users[userId] = {
    ...userState,
    pending: userState.pending.filter((entry) => entry.knowledgeItemId !== normalizedId),
  };
  writeState(state);
  removePendingJobNotification({
    kind: "knowledge",
    userId,
    knowledgeItemId: normalizedId,
    status: "processing",
    title: tracked.title,
    type: tracked.type,
  });
}

function localDate(value: string): string {
  const date = new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function timeLabel(value: string): string {
  const date = new Date(value);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

export function readKnowledgeHomeActivityLogs(
  rawUserId: string | null | undefined,
  now = new Date()
): { today: KnowledgeHomeActivityLog[]; yesterday: KnowledgeHomeActivityLog[] } {
  const userId = normalizeUserId(rawUserId);
  if (!userId) return { today: [], yesterday: [] };
  const userState = currentUserState(readState(), userId);
  const today = localDate(now.toISOString());
  const yesterday = localDate(new Date(now.getTime() - 86400000).toISOString());
  const logs = [
    ...userState.pending.map((item): KnowledgeHomeActivityLog & { occurredAt: string } => ({
      id: `knowledge:${item.knowledgeItemId}:processing`,
      text: `「${item.title}」をAIで使う準備をしています`,
      time: timeLabel(item.startedAt),
      activityAt: item.startedAt,
      to: `/request/knowledge/${encodeURIComponent(item.knowledgeItemId)}`,
      isActive: true,
      occurredAt: item.startedAt,
    })),
    ...userState.reports.map((item): KnowledgeHomeActivityLog & { occurredAt: string } => ({
      id: `knowledge:${item.knowledgeItemId}:${item.status}:${item.occurredAt}`,
      text: item.status === "completed"
        ? `「${item.title}」をAIで使えるようになりました`
        : `「${item.title}」の準備を完了できませんでした`,
      time: timeLabel(item.occurredAt),
      activityAt: item.occurredAt,
      to: `/request/knowledge/${encodeURIComponent(item.knowledgeItemId)}`,
      isActive: false,
      occurredAt: item.occurredAt,
    })),
  ].sort((left, right) => Date.parse(right.occurredAt) - Date.parse(left.occurredAt));

  return {
    today: logs.filter((log) => localDate(log.occurredAt) === today),
    yesterday: logs.filter((log) => localDate(log.occurredAt) === yesterday),
  };
}
